/**
 * The "Live Timelogs" report's data layer — the twin of `services/reports.ts`.
 *
 * The report is a pure consumer of ERPat's BioTime Live Timelogs
 * (`modules/Biotime/specs/biotime-realtime-logs-contract.md` §8, §9): it never
 * measures, pairs or re-orders punches. What this file adds on top of the
 * service (`biotimeRealtimeService`) and the mappers (`api/biotime-realtime.ts`)
 * is the client half of §9:
 *
 *  - {@link loadRealtimeLogsPage} — the 202 loop. ERPat answers 202 while some
 *    day of the window is still being fetched from BioTime; the client repeats
 *    the SAME request after `meta.retry_after` seconds, for at most 90 seconds
 *    from the first ask, reporting days ready / days total as it goes.
 *  - {@link realtimeLogsAvailability} — whether the report is offered at all:
 *    the attendance-viewing capability first (no request when the account
 *    plainly lacks it), then the filters endpoint as the probe. The GATE is
 *    remembered for the current session; the filters it carries (`today`
 *    above all) are not trusted for that long, and a "no" is re-asked when it
 *    is old enough — {@link refreshRealtimeLogsAvailability} does both.
 *  - {@link describeRealtimeLogsError} — fixed copy for the §8.3 error codes.
 *    Never a server message: an upstream failure could carry a host name.
 *  - {@link daysInWindow} / {@link clampWindow} — the date-window rules the page
 *    applies before asking (the server applies them again, §3.1).
 *
 * PERSONAL DATA. Employee codes, names and punch times identify people.
 * Nothing here logs, stores or reports them; the availability cache holds only
 * the filters answer, in memory, for one session, and a sign-out forgets it
 * ({@link forgetRealtimeLogsAvailability}).
 */
import { ErpatApiError } from '@erpat/sdk';
import { biotimeRealtimeService, capabilitiesService } from '@/services/erpat';
import { getSessionEpoch } from '@/services/api/erpat-client';
import { errorMessage, isSessionCancelled } from '@/services/api/http';
import { getSessionScope } from '@/services/session-scope';
import {
  REALTIME_DEFAULT_RETRY_AFTER_SECONDS,
  REALTIME_MAX_DAYS_MAX,
  REALTIME_MAX_DAYS_MIN,
  civilDayNumber,
  isCivilDate,
  type RealtimeLogFiltersVM,
  type RealtimeLogsPage,
  type RealtimeLogsQuery,
} from '@/services/api/biotime-realtime';

// ── The 202 loop (§9 "202 handling") ─────────────────────────────────────────

/**
 * How long the loop keeps asking, measured from the FIRST ask (§9 "up to 90 s").
 * Elapsed time, not the sum of the waits: every 202 is one bounded warm step on
 * the server (§5.10), so an answer itself takes seconds, and counting only the
 * waits let the spinner run for four to seven minutes.
 */
export const REALTIME_LOGS_MAX_WAIT_SECONDS = 90;

/** Shortest wait between two asks, whatever `retry_after` says. */
export const REALTIME_LOGS_MIN_WAIT_SECONDS = 1;

/**
 * A clock that only moves forward — the 202 loop's elapsed time, so a change to
 * the phone's clock can neither end the loop early nor stretch it. `Date.now()`
 * only where no monotonic clock exists. NOT used for the age of the remembered
 * filters: on several platforms it stops while the phone sleeps, which is
 * exactly the night a `today` goes stale (see {@link filtersAgeMs}).
 */
function monotonicNow(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();
}

/** How far the fetch has got — what the page shows while ERPat is still warming the window. */
export interface RealtimeLogsProgress {
  /** Days of the window ERPat can already serve. */
  daysReady: number;
  /** Calendar days in the requested window, inclusive. */
  daysTotal: number;
  /** The dates (`YYYY-MM-DD`) still being fetched. */
  pendingDays: string[];
}

/** One page of the report, as {@link loadRealtimeLogsPage} settled it. */
export interface RealtimeLogsLoadResult extends RealtimeLogsPage {
  /**
   * True when the window was still incomplete
   * {@link REALTIME_LOGS_MAX_WAIT_SECONDS} after the first ask. The page is then
   * the LAST (incomplete) answer — no rows — and the report says it is still
   * loading rather than that there is nothing.
   */
  timedOut: boolean;
  /** Progress as of the last answer. */
  progress: RealtimeLogsProgress;
}

export interface LoadRealtimeLogsOptions {
  /** Aborts the request in flight and any wait at once (rejects with an `AbortError`). */
  signal?: AbortSignal;
  /** Called after EVERY answer, the incomplete ones included. A throw here is ignored. */
  onProgress?: (progress: RealtimeLogsProgress) => void;
  /**
   * Called with EVERY answer as it lands — each 202 and the final 200 — before
   * any wait, and never once `signal` is aborted. The report reads each
   * answer's `meta.access` here, so a scope that moved mid-loop is seen on the
   * 202 that first carried it, not after the loop settles. A throw is ignored.
   * The callback may abort `signal`: the loop then ends without asking again.
   */
  onAnswer?: (page: RealtimeLogsPage) => void;
}

/** The `DOMException` every consumer (`useAsyncData`, `useServerPagedList`) already treats as "not an error". */
function abortError(): DOMException {
  return new DOMException('The Live Timelogs request was aborted.', 'AbortError');
}

/** Wait `seconds`; an abort ends the wait at once and rejects with an `AbortError`. */
function waitSeconds(seconds: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onAbort = (): void => {
      if (timer !== null) clearTimeout(timer);
      reject(abortError());
    };
    timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, seconds * 1000);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Calendar days the progress counts against: the requested window when the
 * query names a valid one, else the window the server resolved (an empty query
 * means today), else one day.
 */
function progressDaysTotal(query: RealtimeLogsQuery, page: RealtimeLogsPage): number {
  const start = query.start_date || query.end_date || '';
  const end = query.end_date || query.start_date || '';
  const requested = daysInWindow(start, end);
  if (requested > 0) return requested;
  const served = daysInWindow(page.meta.startDate, page.meta.endDate);
  return served > 0 ? served : 1;
}

function progressOf(query: RealtimeLogsQuery, page: RealtimeLogsPage): RealtimeLogsProgress {
  const daysTotal = progressDaysTotal(query, page);
  const pendingDays = [...page.meta.pendingDays];
  const daysReady = page.meta.complete ? daysTotal : Math.max(0, daysTotal - pendingDays.length);
  return { daysReady, daysTotal, pendingDays };
}

/**
 * Read one page of the report, riding out ERPat's 202s (§9).
 *
 * Asks; while `meta.complete` is false, waits `max(1, meta.retry_after)`
 * seconds (3 when the server says nothing) and asks the SAME question again.
 * The budget is {@link REALTIME_LOGS_MAX_WAIT_SECONDS} of ELAPSED time from the
 * first ask — the time each answer takes counts, not only the waits. The last
 * wait is shortened so no ask starts after the limit, one final ask follows it,
 * and then the last answer is returned with `timedOut: true`. An ask already in
 * flight at the limit is allowed to finish (its answer is kept if complete), so
 * the loop settles by the limit plus one response at most. Less than a second
 * left is not worth a wait: the loop stops there.
 *
 * NOT retried here: a thrown error (a 4xx, a 5xx, a transport failure). It
 * propagates on the first throw; {@link describeRealtimeLogsError} turns it
 * into copy, and the user decides whether to try again. An abort — during a
 * wait or during a request — rejects with an `AbortError`.
 */
export async function loadRealtimeLogsPage(
  query: RealtimeLogsQuery,
  options: LoadRealtimeLogsOptions = {},
): Promise<RealtimeLogsLoadResult> {
  const { signal, onProgress, onAnswer } = options;

  const ask = async (): Promise<RealtimeLogsPage> => {
    if (signal?.aborted) throw abortError();
    let page: RealtimeLogsPage;
    try {
      page = await biotimeRealtimeService.list(query, signal);
    } catch (err) {
      // The SDK reports an aborted fetch as a status-0 transport error; name it
      // for what it is, so no caller mistakes a cancel for "offline".
      if (signal?.aborted) throw abortError();
      throw err;
    }
    if (!signal?.aborted) {
      try {
        onAnswer?.(page);
      } catch {
        /* an observer must never break the load */
      }
    }
    return page;
  };

  const report = (page: RealtimeLogsPage): RealtimeLogsProgress => {
    const progress = progressOf(query, page);
    try {
      onProgress?.({ ...progress, pendingDays: [...progress.pendingDays] });
    } catch {
      /* a progress display must never break the load */
    }
    return progress;
  };

  // Before the first ask: its own response time counts against the budget too.
  const startedAt = monotonicNow();
  let page = await ask();
  let progress = report(page);

  while (!page.meta.complete) {
    const remaining = REALTIME_LOGS_MAX_WAIT_SECONDS - (monotonicNow() - startedAt) / 1000;
    // Under a second left: a wait that short would put two asks back to back.
    if (remaining < REALTIME_LOGS_MIN_WAIT_SECONDS) {
      return { ...page, timedOut: true, progress };
    }
    const asked = page.meta.retryAfter ?? REALTIME_DEFAULT_RETRY_AFTER_SECONDS;
    const delay = Math.min(Math.max(REALTIME_LOGS_MIN_WAIT_SECONDS, asked), remaining);
    await waitSeconds(delay, signal);
    page = await ask();
    progress = report(page);
  }

  return { ...page, timedOut: false, progress };
}

// ── Availability (§9 "Audience") ────────────────────────────────────────────

/**
 * Why the report is (not) offered:
 *  - `granted`   — the filters endpoint answered 200;
 *  - `no_permit` — the account cannot view team attendance
 *                  (`capabilities.canViewTeamAttendance`: the `attendance`
 *                  permit, or an administrator — the SAME authority both routes
 *                  gate on since Biotime 1.11.0; the web tab's
 *                  `biotime_transactions` permit plays no part). No request
 *                  was made;
 *  - `refused`   — the filters endpoint answered 403 / 404 (a module off, no
 *                  connection, or ERPat disagrees with the capabilities map);
 *  - `failed`    — the question could not be answered (offline, 5xx). NOT
 *                  remembered: the next call asks again.
 */
export type RealtimeLogsAvailabilityReason = 'granted' | 'no_permit' | 'refused' | 'failed';

export interface RealtimeLogsAvailability {
  available: boolean;
  /**
   * The §8.5 answer when `available`, else null — as of the last time it was
   * READ, which is not the whole session: `today` rolls over at the BioTime
   * connection's midnight. {@link refreshRealtimeLogsAvailability} re-reads it.
   */
  filters: RealtimeLogFiltersVM | null;
  reason: RealtimeLogsAvailabilityReason;
}

/**
 * Re-read the filters at most this often when a page is (re)entered or brought
 * back to the foreground — enough to fold the mount and the first
 * `ionViewDidEnter` of one visit into a single request.
 */
export const REALTIME_FILTERS_REVALIDATE_MS = 10_000;

/**
 * The phone's clock, read in the BioTime zone, says a new day has begun but the
 * filters still name the old one: re-read at most this often. A trigger only —
 * the new `today` always comes from the server. The bound keeps a phone whose
 * clock is simply wrong from re-reading every minute.
 */
export const REALTIME_FILTERS_RECHECK_MS = 2 * 60_000;

/**
 * Re-read filters older than this even when nothing suggests the day changed —
 * the backstop for a phone clock that is wrong in the one way the day check
 * cannot see.
 */
export const REALTIME_FILTERS_MAX_AGE_MS = 15 * 60_000;

/**
 * The identity a remembered answer belongs to: tenant + user (the session
 * scope snapshot) AND the session epoch. The epoch is bumped by every
 * `clearSession()` — every sign-out, expiry and company switch — so even the
 * SAME user signing back in to the same company asks again: their permits may
 * have changed while they were away.
 */
function availabilityKey(): string {
  const { companyKey, userId } = getSessionScope();
  return `${companyKey}|${userId}|${getSessionEpoch()}`;
}

/**
 * The remembered answer. The GATE (`available` / `reason`) holds for the
 * session identity until it is re-asked; `answeredAt` (`Date.now()`) is when
 * it was settled — for an OFFERED report that is when its filters were read
 * (those, `today` above all, go stale sooner than the gate), and for a
 * withheld one it is when the "no" was given (a "no" is re-asked once it is
 * old enough: a permit can be granted while the app stays signed in).
 */
let cached: { key: string; answer: RealtimeLogsAvailability; answeredAt: number } | null = null;
let inFlight: { key: string; promise: Promise<RealtimeLogsAvailability> } | null = null;
let refreshing: { key: string; promise: Promise<RealtimeLogsAvailability> } | null = null;

function isUnavailableStatus(err: unknown): boolean {
  return err instanceof ErpatApiError && (err.status === 403 || err.status === 404);
}

/** Remember `answer` for `key` — only while `key` is still the current session. */
function remember(key: string, answer: RealtimeLogsAvailability): void {
  if (availabilityKey() === key) cached = { key, answer, answeredAt: Date.now() };
}

/**
 * How long ago an answer was settled. The WALL clock on purpose: it keeps
 * running while the phone sleeps overnight, where a monotonic clock may not. A
 * clock set backwards gives a negative age — read as "infinitely old", so the
 * worst a clock change can cost is one extra re-read, never a stale `today`.
 */
function answerAgeMs(answeredAt: number): number {
  const age = Date.now() - answeredAt;
  return age >= 0 ? age : Number.POSITIVE_INFINITY;
}

/** Ask the two questions once. Never rejects. */
async function probeAvailability(key: string): Promise<RealtimeLogsAvailability> {
  let answer: RealtimeLogsAvailability;
  let settled = true;

  try {
    const caps = await capabilitiesService.summary();
    // The attendance-viewing capability — `is_admin || attendance`, with the
    // attendance module on — is exactly what both routes `requirePermit()`
    // (contract §8.4, Biotime 1.11.0). It is admin-aware already.
    if (!caps.canViewTeamAttendance) {
      answer = { available: false, filters: null, reason: 'no_permit' };
    } else {
      try {
        const filters = await biotimeRealtimeService.filters();
        answer = { available: true, filters, reason: 'granted' };
      } catch (err) {
        if (isUnavailableStatus(err)) {
          answer = { available: false, filters: null, reason: 'refused' };
        } else {
          answer = { available: false, filters: null, reason: 'failed' };
          settled = false;
        }
      }
    }
  } catch {
    // The capabilities map itself could not be read — no verdict at all.
    answer = { available: false, filters: null, reason: 'failed' };
    settled = false;
  }

  // Remember a SETTLED answer — and only for the session that asked. A probe
  // that lands after a sign-out or a company switch must not seed the next
  // session with the previous one's verdict.
  if (settled) remember(key, answer);
  return answer;
}

/** Follow a shared promise, but let THIS caller walk away on its own signal. */
function followWithSignal<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(abortError());
  return new Promise<T>((resolve, reject) => {
    const onAbort = (): void => reject(abortError());
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      (err: unknown) => {
        signal.removeEventListener('abort', onAbort);
        reject(err);
      },
    );
  });
}

/** The probe for `key`, shared by every caller that asks while it runs. */
function probeShared(key: string, signal?: AbortSignal): Promise<RealtimeLogsAvailability> {
  if (!inFlight || inFlight.key !== key) {
    const promise: Promise<RealtimeLogsAvailability> = probeAvailability(key).finally(() => {
      if (inFlight?.promise === promise) inFlight = null;
    });
    inFlight = { key, promise };
  }
  return followWithSignal(inFlight.promise, signal);
}

/**
 * Is the Live Timelogs report offered to this account? (§9 "Audience")
 *
 * 1. `capabilitiesService.summary()`: only an account that can view team
 *    attendance (`canViewTeamAttendance` — an administrator, or the
 *    `attendance` permit with the module on) gets further. Anyone else is
 *    answered `available: false` WITHOUT a probe — no request an ordinary
 *    employee's app makes is refused by design.
 * 2. `biotimeRealtimeService.filters()`: 200 → offered, with its filters (the
 *    pickers already scoped to the caller's attendance access, and `access`);
 *    403 / 404 → not offered (a module is off, there is no connection, or the
 *    server's view of the permit differs).
 * 3. Anything else (offline, 5xx, a capabilities failure) → not offered, and
 *    NOT remembered, so the next call asks again.
 *
 * A settled answer is remembered in memory for the current session identity
 * (tenant + user + session epoch — see {@link availabilityKey}); a sign-out, a
 * different user or a different company always asks again. Concurrent calls
 * share one probe; each caller's `signal` only detaches that caller (rejecting
 * it with an `AbortError`) — the shared probe runs on and is remembered.
 *
 * This answers from memory once there is an answer — including its filters,
 * however old, and a "no" however old. A page that shows the report calls
 * {@link refreshRealtimeLogsAvailability} to keep `today` current; a page that
 * could offer it calls the same to re-ask a "no" that has aged.
 */
export function realtimeLogsAvailability(signal?: AbortSignal): Promise<RealtimeLogsAvailability> {
  if (signal?.aborted) return Promise.reject(abortError());
  const key = availabilityKey();
  if (cached && cached.key === key) return Promise.resolve(cached.answer);
  return probeShared(key, signal);
}

export interface RefreshRealtimeLogsOptions {
  /** Detaches THIS caller only (rejects it with an `AbortError`); a shared re-read runs on. */
  signal?: AbortSignal;
  /**
   * Answer from memory while the remembered answer was settled less than this
   * long ago (ms) — its filters read, or its "no" given. 0 — the default —
   * always asks again.
   */
  minAgeMs?: number;
}

/** Re-read §8.5 for a session whose gate is open. Never rejects. */
async function rereadFilters(key: string, previous: RealtimeLogsAvailability): Promise<RealtimeLogsAvailability> {
  try {
    const filters = await biotimeRealtimeService.filters();
    const answer: RealtimeLogsAvailability = { available: true, filters, reason: 'granted' };
    remember(key, answer);
    return answer;
  } catch (err) {
    if (isUnavailableStatus(err)) {
      // The server's verdict has changed (the permit was removed, the module
      // switched off, the connection deleted): the report is withdrawn.
      const answer: RealtimeLogsAvailability = { available: false, filters: null, reason: 'refused' };
      remember(key, answer);
      return answer;
    }
    // Offline, a 5xx, a cancelled session: keep the last known answer. A blip
    // must never take an open report away.
    return cached && cached.key === key ? cached.answer : previous;
  }
}

/**
 * Keep the remembered answer current — `today` above all, which rolls over at
 * the BioTime connection's midnight while a session can last for days; and a
 * "no", which a permit granted or a module switched on mid-session turns into
 * a "yes" without any sign-out to forget it.
 *
 * - No answer yet for this session → the ordinary probe
 *   ({@link realtimeLogsAvailability}).
 * - The remembered answer is younger than `minAgeMs` → that answer, with no
 *   request.
 * - The report is not offered (`no_permit`, `refused`) → the FULL probe again
 *   (capabilities, then the filters endpoint), shared with any probe in
 *   flight: a settled "yes" or "no" replaces the remembered one; a failure
 *   leaves the remembered "no" in place. A "no" is thus re-asked only on the
 *   host's own occasions (opening Reports, coming back to the foreground) and
 *   only once it has aged — never on a timer.
 * - Offered → `GET /v1/api/biotime/live-timelogs/filters` again (it reads
 *   ERPat's synced lists only — no BioTime call, §8.5):
 *   - 200 → the new answer replaces the remembered one (a new object);
 *   - 403 / 404 → `refused`, remembered: the report is withdrawn;
 *   - anything else → the LAST KNOWN answer, unchanged.
 *
 * The new `today` is always the server's. Nothing here derives a date from the
 * phone's clock. Concurrent calls share one re-read, and an answer that lands
 * after a session change is never remembered.
 */
export function refreshRealtimeLogsAvailability(
  options: RefreshRealtimeLogsOptions = {},
): Promise<RealtimeLogsAvailability> {
  const { signal, minAgeMs = 0 } = options;
  if (signal?.aborted) return Promise.reject(abortError());
  const key = availabilityKey();
  const entry = cached && cached.key === key ? cached : null;
  if (!entry) return realtimeLogsAvailability(signal);
  if (answerAgeMs(entry.answeredAt) < Math.max(0, minAgeMs)) return Promise.resolve(entry.answer);
  if (!entry.answer.available) return probeShared(key, signal);

  if (!refreshing || refreshing.key !== key) {
    const promise: Promise<RealtimeLogsAvailability> = rereadFilters(key, entry.answer).finally(() => {
      if (refreshing?.promise === promise) refreshing = null;
    });
    refreshing = { key, promise };
  }
  return followWithSignal(refreshing.promise, signal);
}

/**
 * The remembered answer for the CURRENT session, or null when there is none —
 * so a page can render its picker at once, before the async call settles.
 */
export function cachedRealtimeLogsAvailability(): RealtimeLogsAvailability | null {
  return cached && cached.key === availabilityKey() ? cached.answer : null;
}

/**
 * Forget the remembered answer — its filters hold up to 2000 employee names and
 * codes — and any probe or re-read in flight. Called by the single sign-out
 * choke point (`session.store.signOutLocal()`: sign-out, expiry, company
 * switch). The session key already stops an old answer from being SERVED to
 * the next session; this makes sure it is not even held.
 */
export function forgetRealtimeLogsAvailability(): void {
  cached = null;
  inFlight = null;
  refreshing = null;
}

/** True while an answer (for any session) is held in memory. For specs. */
export function realtimeLogsAvailabilityHeld(): boolean {
  return cached !== null;
}

/** Forget the remembered answer and anything in flight. For specs. */
export function resetRealtimeLogsAvailability(): void {
  forgetRealtimeLogsAvailability();
}

// ── Errors (§8.3) ────────────────────────────────────────────────────────────

/** What the report says when a load fails. FIXED copy — never the server's message. */
export interface RealtimeLogsErrorCopy {
  title: string;
  message: string;
  /** True when trying again may help; the page offers a retry button only then. */
  retryable: boolean;
  /** The server's `error.code` ('' for a transport failure or a non-API error). */
  code: string;
  /** `details.retry_after` seconds when the server gave one, else null. */
  retryAfter: number | null;
}

const COPY_UPSTREAM_DOWN = {
  title: "BioTime isn't answering",
  message: "Your company's BioTime server didn't respond just now. Try again shortly.",
};
const COPY_CONNECTION = {
  title: 'BioTime connection needs attention',
  message: "ERPat can't read your company's BioTime server right now. Ask your administrator to check the connection.",
};
const COPY_CACHE = {
  title: 'Live Timelogs is temporarily unavailable',
  message: "ERPat couldn't prepare the logs right now. Ask your administrator if this keeps happening.",
};
const COPY_OFFLINE = {
  title: "You're offline",
  message: 'Check your connection and try again.',
};

function copy(
  base: { title: string; message: string },
  retryable: boolean,
  code: string,
  retryAfter: number | null = null,
): RealtimeLogsErrorCopy {
  return { title: base.title, message: base.message, retryable, code, retryAfter };
}

/** A positive whole number from `details`, else null. */
function detailNumber(details: Record<string, unknown> | undefined, key: string): number | null {
  const raw = details?.[key];
  const n = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : NaN;
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
}

/**
 * Turn a thrown error into the report's copy, by the §8.3 `code`.
 *
 * The copy is FIXED. The server's `message` is never shown: an upstream
 * failure is described in ERPat's words for a reason (it could otherwise
 * surface the BioTime host), and the one variable this uses —
 * `details.max_days` — is a number.
 */
export function describeRealtimeLogsError(err: unknown): RealtimeLogsErrorCopy {
  if (err instanceof DOMException && err.name === 'AbortError') {
    return copy({ title: 'Loading stopped', message: 'The request was cancelled.' }, true, '');
  }
  if (isSessionCancelled(err)) {
    return copy({ title: 'Signed out', message: 'Sign in again to see Live Timelogs.' }, false, '');
  }
  if (!(err instanceof ErpatApiError)) {
    return copy(
      { title: "Live Timelogs couldn't load", message: 'Something went wrong. Try again.' },
      true,
      '',
    );
  }

  const code = err.code ?? '';
  const retryAfter = detailNumber(err.details, 'retry_after');

  switch (code) {
    case 'upstream_unavailable':
    case 'upstream_rate_limited':
      return copy(COPY_UPSTREAM_DOWN, true, code, retryAfter);
    case 'upstream_auth_failed':
    case 'upstream_locked':
    case 'upstream_blocked':
    case 'not_configured':
      return copy(COPY_CONNECTION, false, code);
    case 'cache_unavailable':
      return copy(COPY_CACHE, false, code);
    case 'validation_failed': {
      const maxDays = detailNumber(err.details, 'max_days');
      const message =
        maxDays !== null
          ? `Choose a range of ${maxDays} ${maxDays === 1 ? 'day' : 'days'} or fewer.`
          : 'Check the dates and filters, then try again.';
      return copy({ title: "That date range can't be shown", message }, false, code);
    }
    case 'invalid_response':
      // App copy (`errorMessage()` has no server text for this code). A 2xx/3xx
      // that was not JSON is a web page — a tenant fault, not worth a retry; an
      // unreadable error status may well read the next time.
      return copy({ title: "Live Timelogs couldn't be read", message: errorMessage(err) }, err.status >= 400, code);
    default:
      break;
  }

  if (err.status === 0) return copy(COPY_OFFLINE, true, code);
  if (err.status === 403 || err.status === 404) {
    return copy(
      {
        title: "Live Timelogs isn't available",
        message: "This account can't open Live Timelogs. Ask your administrator if it should.",
      },
      false,
      code,
    );
  }
  if (err.status === 429) {
    return copy(
      { title: 'Please wait a moment', message: 'Too many requests were made. Try again shortly.' },
      true,
      code,
      retryAfter,
    );
  }
  if (err.status >= 500) {
    return copy(
      { title: "Live Timelogs couldn't load", message: 'Something went wrong on the server. Try again.' },
      true,
      code,
    );
  }
  return copy({ title: "Live Timelogs couldn't load", message: 'Something went wrong. Try again.' }, true, code);
}

// ── The date window (§3.1, mirrored) ─────────────────────────────────────────

/**
 * Calendar days in `[start, end]`, inclusive; 0 when either is not a real
 * `YYYY-MM-DD` date or `end` is before `start`. Pure day arithmetic
 * ({@link civilDayNumber}) — no `Date`, so no timezone or DST can move it.
 */
export function daysInWindow(start: string, end: string): number {
  const a = civilDayNumber(start);
  const b = civilDayNumber(end);
  if (a === null || b === null || b < a) return 0;
  return b - a + 1;
}

/** Why {@link clampWindow} refused a window ('' = it did not). */
export type RealtimeWindowError = '' | 'order' | 'future' | 'too_large';

export interface ClampedWindow {
  start: string;
  end: string;
  error: RealtimeWindowError;
}

/**
 * The window the page may ask for, by the server's own rules (§3.1
 * `biotime_realtime_resolve_window()`), so a refusal is shown before the
 * request rather than after it:
 *
 *  - both dates empty (or malformed) → `today..today`; one → the other's value;
 *  - an `end` after `today` is CAPPED to `today` (not an error);
 *  - a `start` after `today` → `'future'`;
 *  - a `start` after `end` → `'order'`;
 *  - more than `maxDays` days → `'too_large'` — REFUSED, never shortened.
 *
 * `today` is the BioTime connection's date (`filters.today`), not the phone's.
 * `maxDays` outside 1..31 is clamped into it. The returned `start` / `end` are
 * the resolved values even when `error` is set, so the page can keep showing
 * what was chosen.
 */
export function clampWindow(start: string, end: string, today: string, maxDays: number): ClampedWindow {
  let from = isCivilDate(start) ? start : '';
  let to = isCivilDate(end) ? end : '';
  const todayKey = isCivilDate(today) ? today : '';

  if (from === '' && to === '') {
    from = todayKey;
    to = todayKey;
  } else if (from === '') {
    from = to;
  } else if (to === '') {
    to = from;
  }

  if (from === '' || to === '') return { start: from, end: to, error: '' };

  const limit = Number.isFinite(maxDays)
    ? Math.min(REALTIME_MAX_DAYS_MAX, Math.max(REALTIME_MAX_DAYS_MIN, Math.floor(maxDays)))
    : REALTIME_MAX_DAYS_MAX;

  const todayNum = civilDayNumber(todayKey);
  const fromNum = civilDayNumber(from);
  if (todayNum !== null) {
    if ((civilDayNumber(to) ?? 0) > todayNum) to = todayKey;
    if (fromNum !== null && fromNum > todayNum) return { start: from, end: to, error: 'future' };
  }

  const days = daysInWindow(from, to);
  if (days === 0) return { start: from, end: to, error: 'order' };
  if (days > limit) return { start: from, end: to, error: 'too_large' };
  return { start: from, end: to, error: '' };
}
